import { useCallback, useMemo, useState } from 'react';
import { useLotsByUris } from 'src/hooks/services/useLot';
import useMaterialTypeBlendingBlock from 'src/hooks/useMaterialTypeBlendingBlock';
import useBlendAcknowledgementStore, {
  NO_ACKNOWLEDGEMENTS,
} from 'src/stores/useBlendAcknowledgementStore';
import {
  BLEND_ATTRIBUTE,
  buildBlendCheckKey,
  compareBlendAttributes,
  getBlendedBatchAttributes,
} from 'src/utils/blendAttributes';
import { MATERIAL_BATCH_POWDER_QUALITY_STATUSES } from 'src/utils/constants';
import { getContainerDisplayName, getContainerName } from 'src/utils/stringUtils';
import { getShortUuid } from 'src/utils/url';

export const BLEND_CHECK_MODE = {
  NONE: 'none',
  COMPARISON: 'comparison',
  BLENDED_BATCH: 'blendedBatch',
};

export const BLEND_RESOURCE_TYPE = {
  CONTAINER: 'container',
  PRINTER: 'printer',
  BATCH: 'batch',
};

/**
 * The `source` / `destination` descriptors, built from the resource itself.
 *
 * Naming a resource takes three fields that have to agree - and half the flows
 * pick their side at runtime, which used to mean writing the same condition three
 * times and getting `name` from one helper and `nameWithType` from another.
 * Choose the builder once instead.
 */
export const blendResource = {
  batch: batch => ({
    name: `Batch ${getShortUuid(batch?.uri || '')}`,
    type: BLEND_RESOURCE_TYPE.BATCH,
  }),
  container: container => ({
    name: getContainerName(container),
    nameWithType: getContainerDisplayName(container),
    type: BLEND_RESOURCE_TYPE.CONTAINER,
  }),
  // A printer's name is already self-describing, so both forms are the same.
  printer: printer => ({
    name: printer?.name,
    nameWithType: printer?.name,
    type: BLEND_RESOURCE_TYPE.PRINTER,
  }),
};

// Stable reference: a fresh [] every render would re-trigger the lot query.
const EMPTY_URIS = [];

/**
 * Lot names, keyed by uri. They live on the material-lot resource rather than on
 * the batch, so they need a fetch - but only when a lot difference will actually
 * be shown. `useLotsByUris` is cached by react-query, so repeat views cost nothing.
 */
const useLotNames = differences => {
  const lotUris = useMemo(() => {
    const lots = differences.find(attribute => attribute.key === BLEND_ATTRIBUTE.MATERIAL_LOT);

    if (!lots) {
      return EMPTY_URIS;
    }

    const bothSides = [...(lots.source || []), ...(lots.destination || [])];
    return [...new Set(bothSides)];
  }, [differences]);

  const { data: materialLots = [] } = useLotsByUris(lotUris);

  return useMemo(() => Object.fromEntries(materialLots.map(lot => [lot.uri, lot])), [materialLots]);
};

/**
 * One check for every action that blends material
 *
 * Two modes, one UI:
 * - COMPARISON, when a source is going into a destination that already holds
 *   material. Compares the four attributes and gates the action until each
 *   difference is acknowledged.
 * - BLENDED_BATCH, when the batch being used is itself a blend.
 *   e.g. loading an empty printer. Opt in with `checkBlendedBatch`.
 *
 * The two are deliberately NOT inferred from each other: an empty destination
 * means "nothing to compare", not "check the source for internal blending".
 *
 * The FF `block-material-type-blending` hard block is layered on top:
 * when it fires there is no acknowledge path at all.
 *
 * @param {object} sourceBatch          batch being added
 * @param {object} [destinationBatch]   batch already in the destination; absent means no comparison
 * @param {object} source               { name, type } of the source resource
 * @param {object} [destination]        { name, type } of the destination resource
 * @param {string} actionLabel          verb used in the blocked copy, e.g. 'top off'
 * @param {boolean} [checkBlendedBatch] warn when the source batch is itself a blend
 * @param {boolean} [sievesSource]      the action sieves the source before blending it
 * @param {boolean} [isLoading]         the batches are still being fetched
 * @param {boolean} [enabled]           false disables the whole check
 */
export default function useBlendCheck({
  sourceBatch,
  destinationBatch,
  source,
  destination,
  actionLabel,
  checkBlendedBatch = false,
  sievesSource = false,
  isLoading = false,
  enabled = true,
}) {
  const [isReviewOpen, setReviewOpen] = useState(false);

  // Pages commonly default a missing batch to `{}` (truthy), which would otherwise
  // read as "a batch with no materials" and report every attribute as differing.
  const rawFrom = sourceBatch?.uri ? sourceBatch : null;
  const into = destinationBatch?.uri ? destinationBatch : null;

  // A sieve action marks the material it produces `sieved` before loading it
  // so the powder that actually meets the destination is never the source's current quality.
  const from = useMemo(() => {
    if (!sievesSource || !rawFrom) {
      return rawFrom;
    }
    // eslint-disable-next-line camelcase
    return { ...rawFrom, powder_quality: MATERIAL_BATCH_POWDER_QUALITY_STATUSES.SIEVED };
  }, [sievesSource, rawFrom]);

  const isComparison = Boolean(enabled && from && into);
  const isBlendedBatchCheck = Boolean(enabled && checkBlendedBatch && from && !into);

  const isBlocked = useMaterialTypeBlendingBlock(
    isComparison ? from : null,
    isComparison ? into : null
  );

  const attributes = useMemo(() => {
    if (isComparison) {
      return compareBlendAttributes(from, into);
    }
    return isBlendedBatchCheck ? getBlendedBatchAttributes(from) : [];
  }, [isComparison, isBlendedBatchCheck, from, into]);

  const differences = useMemo(
    () => attributes.filter(attribute => attribute.differs),
    [attributes]
  );

  const mode = useMemo(() => {
    // A comparison with nothing to flag still renders the "materials match"
    // reassurance; a blended-batch check with nothing mixed renders nothing.
    if (isComparison) {
      return BLEND_CHECK_MODE.COMPARISON;
    }
    return isBlendedBatchCheck && differences.length
      ? BLEND_CHECK_MODE.BLENDED_BATCH
      : BLEND_CHECK_MODE.NONE;
  }, [isComparison, isBlendedBatchCheck, differences.length]);

  const lotsByUri = useLotNames(differences);

  // Acknowledgements live in a store, not in this component, so they survive the
  // hand-offs between blend screens (route redirects and stacked MHS panels alike).
  const checkKey = useMemo(
    () => buildBlendCheckKey(mode, from, into, attributes),
    [mode, from, into, attributes]
  );

  const acknowledgedKeys = useBlendAcknowledgementStore(
    state => state.acknowledged[checkKey] ?? NO_ACKNOWLEDGEMENTS
  );
  const acknowledgeAttribute = useBlendAcknowledgementStore(state => state.acknowledge);

  const isAcknowledged = useCallback(key => acknowledgedKeys.includes(key), [acknowledgedKeys]);

  const acknowledge = useCallback(
    key => acknowledgeAttribute(checkKey, key),
    [acknowledgeAttribute, checkKey]
  );

  const openReview = useCallback(() => setReviewOpen(true), []);
  const closeReview = useCallback(() => setReviewOpen(false), []);

  const acknowledgedCount = useMemo(
    () => differences.filter(attribute => acknowledgedKeys.includes(attribute.key)).length,
    [differences, acknowledgedKeys]
  );

  const isReviewComplete = acknowledgedCount === differences.length;

  // Several flows render the panel before their batches arrive. Until they do,
  // "no differences found" and "not looked yet" are indistinguishable, so the
  // action stays closed and the gate says it is still checking.
  const isChecking = Boolean(enabled && isLoading);

  return {
    mode,
    isChecking,
    isBlocked,
    actionLabel,
    source,
    destination,
    attributes,
    differences,
    acknowledgedCount,
    isReviewComplete,
    isAcknowledged,
    acknowledge,
    isReviewOpen,
    openReview,
    closeReview,
    lotsByUri,
    /** The single flag a page needs to gate its submit button. */
    canProceed: !isChecking && !isBlocked && isReviewComplete,
  };
}
